/** @jsxImportSource @emotion/react */
import React from 'react';
import { Button, Card, Spin, Typography } from 'antd';
import {
  ArrowRightOutlined,
  CheckOutlined,
  CloudOutlined,
  HomeOutlined,
  ShopOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { css } from '@emotion/react';
import plotArt from '../../assets/home/plot.webp';

const { Title, Text } = Typography;

const styles = {
  card: css({
    height: '100%',
    overflow: 'hidden',
    background: 'linear-gradient(155deg, #F4FAEC 0%, #E9F5E2 55%, #E2F0DA 100%)',
    border: '1px solid #CFE4C2',
  }),
  // Огород получил СВОЮ колонку, а не подложку: за полупрозрачными строками
  // быстрых действий от него оставались только забор и верхушка дерева.
  layout: css({
    display: 'grid',
    gridTemplateColumns: 'minmax(210px, 6fr) minmax(150px, 4fr) minmax(230px, 6fr)',
    gap: 16,
    padding: 20,
    '@media (max-width: 1200px)': {
      gridTemplateColumns: 'minmax(220px, 5fr) minmax(220px, 4fr)',
    },
    '@media (max-width: 768px)': { gridTemplateColumns: '1fr' },
  }),
  summary: css({
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    minWidth: 0,
    // Иллюстрация лежит под текстом, поэтому текст всегда сверху.
    position: 'relative',
    zIndex: 1,
  }),
  summaryHeading: css({ marginBottom: '0 !important', color: '#2C4A20' }),
  statsList: css({
    margin: 0,
    padding: 0,
    listStyle: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    color: '#3B6129',
    fontWeight: 500,
  }),
  statRow: css({ display: 'flex', alignItems: 'center', gap: 8 }),
  actionsCol: css({
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    position: 'relative',
    zIndex: 1,
  }),
  actionRow: css({
    width: '100%',
    height: 'auto',
    padding: '12px 14px',
    background: '#fff',
    borderRadius: 14,
    border: '1px solid #DCE7C7',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    transition: 'transform 120ms ease, box-shadow 120ms ease',
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: '0 6px 14px rgba(56, 142, 60, 0.14)',
    },
  }),
  actionLeft: css({ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }),
  actionIcon: css({
    width: 36,
    height: 36,
    borderRadius: 12,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#E8F5E9',
    color: '#33691E',
    fontSize: 18,
    flexShrink: 0,
  }),
  actionText: css({ display: 'flex', flexDirection: 'column', minWidth: 0 }),
  emptyState: css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    flexDirection: 'column',
    gap: 12,
    textAlign: 'center',
    position: 'relative',
    zIndex: 1,
  }),
  // Огород прижат к нижнему краю своей колонки и слегка вываливается за него —
  // так карточка выглядит окном в участок, а не плиткой с картинкой.
  artCol: css({
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    minWidth: 0,
    marginBottom: -20,
    '@media (max-width: 1200px)': { display: 'none' },
  }),
  art: css({
    width: '126%',
    maxWidth: 'none',
    display: 'block',
    pointerEvents: 'none',
    userSelect: 'none',
  }),
};

type QuickAction = {
  key: string;
  icon: React.ReactNode;
  title: string;
  hint: string;
  onClick: () => void;
  disabled?: boolean;
};

type Stat = { icon: React.ReactNode; label: string };

type MyPlotCardProps = {
  farmName?: string | null;
  modeLabel?: string;
  modeDescription?: string;
  stats?: Stat[];
  loading?: boolean;
  hasFarm: boolean;
  hasSave: boolean;
  onPrimaryClick?: () => void;
  primaryLabel?: string;
  primaryLoading?: boolean;
  primaryDisabled?: boolean;
  actions?: QuickAction[];
};

const defaultStats: Stat[] = [
  { icon: <HomeOutlined />, label: '— грядок' },
  { icon: <ShopOutlined />, label: '— теплиц' },
  { icon: <ThunderboltOutlined />, label: '— активных сейвов' },
];

const defaultActions = (onClick: () => void): QuickAction[] => [
  {
    key: 'water',
    icon: <CloudOutlined />,
    title: 'Полив',
    hint: 'Скоро: счётчик грядок к поливу',
    onClick,
  },
  {
    key: 'harvest',
    icon: <CheckOutlined />,
    title: 'Сбор урожая',
    hint: 'Скоро: счётчик готовых грядок',
    onClick,
  },
  {
    key: 'greenhouse',
    icon: <ShopOutlined />,
    title: 'Проверить теплицу',
    hint: 'Скоро: статус теплицы',
    onClick,
  },
];

const MyPlotCard: React.FC<MyPlotCardProps> = ({
  farmName,
  modeLabel,
  modeDescription,
  stats,
  loading,
  hasFarm,
  hasSave,
  onPrimaryClick,
  primaryLabel,
  primaryLoading,
  primaryDisabled,
  actions,
}) => {
  const noopOpen = onPrimaryClick || (() => {});
  const resolvedActions = actions?.length ? actions : defaultActions(noopOpen);
  const resolvedStats = stats?.length ? stats : defaultStats;
  const heading = farmName || 'Мой участок';
  const subHeading = modeLabel || (hasFarm ? 'Свободный режим' : 'Создайте ферму, чтобы начать');

  return (
    <Card css={styles.card} styles={{ body: { padding: 0 } }} variant="borderless">
      <div css={styles.layout}>
        <div css={styles.summary}>
          <Title level={3} css={styles.summaryHeading}>
            {heading}
          </Title>
          <Text type="secondary" strong>
            {subHeading}
          </Text>
          {modeDescription ? <Text type="secondary">{modeDescription}</Text> : null}
          {loading ? (
            <Spin size="small" />
          ) : (
            <ul css={styles.statsList}>
              {resolvedStats.map((stat, idx) => (
                <li key={idx} css={styles.statRow}>
                  <span aria-hidden>{stat.icon}</span>
                  <span>{stat.label}</span>
                </li>
              ))}
            </ul>
          )}
          {onPrimaryClick ? (
            <Button
              type="primary"
              icon={<ArrowRightOutlined />}
              onClick={onPrimaryClick}
              loading={primaryLoading}
              disabled={primaryDisabled}
              style={{ alignSelf: 'flex-start', marginTop: 8 }}
            >
              {primaryLabel || (hasSave ? 'Открыть участок' : 'Играть')}
            </Button>
          ) : null}
        </div>

        <div css={styles.artCol}>
          <img src={plotArt} alt="" css={styles.art} />
        </div>

        {hasFarm ? (
          <div css={styles.actionsCol}>
            {resolvedActions.map((action) => (
              <button
                key={action.key}
                type="button"
                css={styles.actionRow}
                onClick={action.disabled ? undefined : action.onClick}
                disabled={action.disabled}
              >
                <span css={styles.actionLeft}>
                  <span css={styles.actionIcon}>{action.icon}</span>
                  <span css={styles.actionText}>
                    <Text strong>{action.title}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {action.hint}
                    </Text>
                  </span>
                </span>
                <ArrowRightOutlined style={{ color: '#9CCC65' }} />
              </button>
            ))}
          </div>
        ) : (
          <div css={styles.emptyState}>
            <Text type="secondary">
              Здесь появятся быстрые действия по участку, как только будет выбрана ферма.
            </Text>
          </div>
        )}
      </div>
    </Card>
  );
};

export default MyPlotCard;
